import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DeviceEventEmitter, StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { useAppSelector } from 'store/hooks';
import { selectEligibleFreeTrialDays, selectIsStillDecidingModal, selectSubscriptionPrices, } from 'features/premium/selectors';
import { tierThemes } from 'features/premium/benefits';
import { Button, rem } from 'design-system';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import FastImage from 'utils/packages/rnFastImage';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { formatAmount } from 'utils/formatting';
import { Amplitude } from 'utils';
import { useActivatePremiumTrialModal } from './useActivatePremiumTrialModal';
const BORDER_RADIUS = rem(24);
const IMG_SIZE = rem(76);
const emmaImage = require('design-system/img/logo-placeholder-gradient.png');
const ModalContent = ({ tierShortName, eligibleFreeTrialDays, tierPrice, currency, onClose, onPressContinue, isFetching, }) => {
    const styles = useStyles(styleSet);
    const descriptionText = `Try out ${tierShortName} with a ${eligibleFreeTrialDays}-day free trial\nand enjoy all of our best features today`;
    const subText = `No payment now. Get ${eligibleFreeTrialDays} days for free, then pay ${tierPrice
        ? formatAmount(parseFloat(tierPrice ?? '0'), currency)
        : `£${tierPrice || ''}`}/year.\nCancel anytime`;
    useEffect(() => {
        Amplitude.logEvent('Still Deciding Modal Shown');
    }, []);
    return (<View>
      <View style={styles.iconContainer}>
        <View style={styles.border}>
          <View style={styles.outer}>
            <View style={styles.inner}>
              <FastImage source={emmaImage} resizeMode="contain" style={staticStyles.logoImage}/>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.closeButtonContainer}>
        <NavigationCloseButton onPress={onClose} colorKey={'secondaryTransparent'}/>
      </View>
      <View style={styles.contentContainer}>
        <Text Text-20 centered>
          Still deciding?
        </Text>
        <Text TextThin-16 centered style={styles.descriptionText}>
          {descriptionText}
        </Text>
        <Button custom brand title="Get started for free!" onPress={onPressContinue} isFetching={isFetching}/>
        <Text TextThin-12 Secondary centered>
          {subText}
        </Text>
      </View>
    </View>);
};
class DismissedModalsStore {
    dismissed = new Set();
    isDismissed(key) {
        return this.dismissed.has(key);
    }
    markDismissed(key) {
        this.dismissed.add(key);
    }
    reset() {
        this.dismissed.clear();
    }
}
const dismissedModalsStore = new DismissedModalsStore();
const DELAY = 5000;
export const useDelayedModal = (screenKey, selectedTier, selectedPeriod, analyticsFlow, onUpgradeComplete, benefitId) => {
    const enabled = useAppSelector(selectIsStillDecidingModal);
    const isPremium = useAppSelector((store) => store.user.user.emmaProStatus).active;
    const prices = useAppSelector(selectSubscriptionPrices);
    const tierPrices = prices?.[selectedPeriod]?.[selectedTier];
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    const tierTheme = tierThemes[selectedTier];
    const delay = Number(enabled?.extra?.timeout) || DELAY;
    const [isDismissed, setIsDismissed] = useState(() => dismissedModalsStore.isDismissed(screenKey));
    const timerRef = useRef(null);
    const isModalOpenRef = useRef(false);
    const isWaitingForSetupIntentRef = useRef(false);
    const showOrUpdateModalRef = useRef(null);
    const globalDismissUnsubscribeRef = useRef(null);
    const isBlockedRef = useRef(false);
    const isFocusedRef = useRef(false);
    const { openUpgradeModalWithSubscriptions, isFetching } = useActivatePremiumTrialModal({
        selectedTier,
        analyticsFlow,
        onUpgradeComplete,
        benefitId,
    });
    const startTimer = useCallback(() => {
        if (!enabled)
            return;
        if (isBlockedRef.current)
            return;
        if (!isFocusedRef.current)
            return;
        if (Modal.isAnyModalOpen?.()) {
            if (globalDismissUnsubscribeRef.current != null)
                return;
            globalDismissUnsubscribeRef.current =
                Modal.addGlobalDismissListener?.(() => {
                    globalDismissUnsubscribeRef.current?.();
                    globalDismissUnsubscribeRef.current = null;
                    startTimer();
                }) ?? null;
            return;
        }
        timerRef.current = setTimeout(() => {
            if (isBlockedRef.current)
                return;
            if (Modal.isAnyModalOpen?.()) {
                if (globalDismissUnsubscribeRef.current == null) {
                    globalDismissUnsubscribeRef.current =
                        Modal.addGlobalDismissListener?.(() => {
                            globalDismissUnsubscribeRef.current?.();
                            globalDismissUnsubscribeRef.current = null;
                            startTimer();
                        }) ?? null;
                }
                return;
            }
            isModalOpenRef.current = true;
            showOrUpdateModalRef.current?.();
        }, delay);
    }, [delay, enabled]);
    const pauseTimer = useCallback(() => {
        globalDismissUnsubscribeRef.current?.();
        globalDismissUnsubscribeRef.current = null;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const resetTimer = useCallback(() => {
        if (isDismissed)
            return;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        startTimer();
    }, [isDismissed, startTimer]);
    const setBlocked = useCallback((blocked) => {
        const wasBlocked = isBlockedRef.current;
        isBlockedRef.current = blocked;
        if (wasBlocked && !blocked) {
            resetTimer();
        }
    }, [resetTimer]);
    const handleModalDismissed = useCallback(() => {
        if (!isDismissed) {
            dismissedModalsStore.markDismissed(screenKey);
            setIsDismissed(true);
        }
    }, [screenKey, isDismissed]);
    const onClose = useCallback(() => {
        isModalOpenRef.current = false;
        Modal.hide();
        handleModalDismissed();
    }, [handleModalDismissed]);
    const onPressContinue = useCallback(() => {
        const subscriptionYearlyProPrice = prices?.[selectedPeriod][selectedTier];
        if (subscriptionYearlyProPrice) {
            isWaitingForSetupIntentRef.current = true;
            openUpgradeModalWithSubscriptions([subscriptionYearlyProPrice], () => { }, false);
        }
    }, [prices, selectedPeriod, selectedTier, openUpgradeModalWithSubscriptions]);
    const showOrUpdateModal = useCallback(() => {
        Modal.show(<ModalContent tierShortName={tierTheme.shortName} eligibleFreeTrialDays={eligibleFreeTrialDays} tierPrice={tierPrices?.price} currency={tierPrices?.currency} onClose={onClose} onPressContinue={onPressContinue} isFetching={isFetching}/>, {
            hideHandle: true,
            onDidHideCallback: () => {
                isModalOpenRef.current = false;
                handleModalDismissed();
            },
            containerStyle: { overflow: 'visible' },
        });
    }, [
        handleModalDismissed,
        tierTheme.shortName,
        eligibleFreeTrialDays,
        tierPrices,
        onClose,
        onPressContinue,
        isFetching,
    ]);
    // Keep ref in sync with latest showOrUpdateModal
    showOrUpdateModalRef.current = showOrUpdateModal;
    useFocusEffect(useCallback(() => {
        isFocusedRef.current = true;
        if (!enabled)
            return;
        if (isDismissed)
            return;
        startTimer();
        // eslint-disable-next-line consistent-return
        return () => {
            isFocusedRef.current = false;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            globalDismissUnsubscribeRef.current?.();
            globalDismissUnsubscribeRef.current = null;
        };
    }, [enabled, isDismissed, startTimer]));
    useEffect(() => {
        if (isPremium) {
            handleModalDismissed();
            onClose();
        }
    }, [
        isPremium,
        handleModalDismissed,
        openUpgradeModalWithSubscriptions,
        tierPrices,
        onClose,
    ]);
    // Update modal content when isFetching changes
    useEffect(() => {
        if (isModalOpenRef.current) {
            showOrUpdateModal();
        }
    }, [isFetching, showOrUpdateModal]);
    // Listen for Stripe setup-intent completion
    useEffect(() => {
        const handleSetupIntentComplete = () => {
            if (isWaitingForSetupIntentRef.current && isModalOpenRef.current) {
                isWaitingForSetupIntentRef.current = false;
                onClose();
            }
        };
        const subscription = DeviceEventEmitter.addListener('STRIPE_SETUP_INTENT_SUCCESS', handleSetupIntentComplete);
        return () => subscription.remove();
    }, [onClose]);
    return {
        pauseTimer,
        resetTimer,
        setBlocked,
    };
};
const staticStyles = StyleSheet.create({
    logoImage: {
        width: '100%',
        height: '100%',
        borderRadius: BORDER_RADIUS,
    },
});
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        position: 'absolute',
        top: rem(-32),
        alignSelf: 'center',
    },
    closeButtonContainer: {
        paddingTop: rem(16),
        paddingBottom: rem(8),
    },
    contentContainer: {
        gap: rem(24),
        paddingTop: rem(32),
    },
    descriptionText: {
        paddingVertical: rem(4),
    },
    inner: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    outer: {
        height: IMG_SIZE,
        width: IMG_SIZE,
        borderRadius: BORDER_RADIUS,
        backgroundColor: colors.background.brandLight,
    },
    border: {
        padding: rem(5.8),
        borderRadius: BORDER_RADIUS + rem(5.8),
        backgroundColor: colors.borders.iconStroke,
        alignSelf: 'flex-start',
    },
}));
